import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const navItems = [
  { to: '/', label: '首页' },
  { to: '/search', label: '搜索' },
  { to: '/about', label: '关于' },
];

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const isActive = (to: string) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-300/80 bg-background-50/92 backdrop-blur-md">
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4 md:h-[72px]">
          <Link to="/" className="flex items-center gap-2.5 whitespace-nowrap">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-background-50">
              <i className="ri-book-open-line text-lg" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-[17px] font-semibold tracking-wide text-foreground-950">成人影视</span>
              <span className="mt-1 font-label text-[9px] tracking-[0.28em] text-foreground-500">ARCHIVE 18+</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            <Link
              to="/"
              className={`rounded-md px-3.5 py-2 text-sm transition-colors ${
                isActive('/') ? 'text-primary-500' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              首页
            </Link>
            <div
              className="relative"
              onMouseEnter={() => setCategoryOpen(true)}
              onMouseLeave={() => setCategoryOpen(false)}
            >
              <button
                type="button"
                onClick={() => setCategoryOpen((v) => !v)}
                aria-expanded={categoryOpen}
                className={`flex cursor-pointer items-center gap-1 rounded-md px-3.5 py-2 text-sm transition-colors ${
                  location.pathname.startsWith('/category')
                    ? 'text-primary-500'
                    : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                分类
                <i className={`ri-arrow-down-s-line text-base transition-transform ${categoryOpen ? 'rotate-180' : ''}`} />
              </button>
              {categoryOpen && (
                <div className="absolute left-1/2 top-full w-[560px] -translate-x-1/2 pt-3">
                  <div className="animate-soft-fade rounded-lg border border-background-300/80 bg-background-100 p-5">
                    <p className="eyebrow mb-4">全部分类</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {categories.map((category) => (
                        <Link
                          key={category.id}
                          to={`/category/${category.id}`}
                          className="group flex items-start gap-3 rounded-md p-2.5 transition-colors hover:bg-background-200"
                        >
                          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-200 text-primary-400 transition-colors group-hover:text-primary-500">
                            <i className={`${category.icon} text-base`} />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm text-foreground-900">{category.name}</span>
                            <span className="block truncate text-[11px] text-foreground-500">
                              {category.children.map((child) => child.name).join(' · ')}
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {navItems.slice(1).map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3.5 py-2 text-sm transition-colors ${
                  isActive(item.to) ? 'text-primary-500' : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/search"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-500/60 hover:text-primary-500"
              aria-label="搜索内容"
            >
              <i className="ri-search-line text-base" />
            </Link>
            <span className="hidden h-9 items-center rounded-md border border-accent-300/60 bg-accent-500/10 px-2.5 font-label text-[10px] tracking-[0.18em] text-accent-400 sm:flex">
              仅限 18+
            </span>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 md:hidden"
              aria-label="打开菜单"
              aria-expanded={mobileOpen}
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`} />
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="animate-soft-fade border-t border-background-300/80 bg-background-50 px-4 pb-8 pt-4 md:hidden">
          <div className="flex flex-col gap-1">
            {[{ to: '/', label: '首页' }, ...navItems.slice(1)].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-3 text-[15px] ${
                  isActive(item.to) ? 'bg-background-200 text-primary-500' : 'text-foreground-800'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
          <p className="eyebrow mb-3 mt-6">分类</p>
          <div className="grid grid-cols-2 gap-2">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                className="flex items-center gap-2 rounded-md border border-background-300/70 bg-background-100 px-3 py-3 text-sm text-foreground-800"
              >
                <i className={`${category.icon} text-base text-primary-400`} />
                {category.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}